import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import SortDirection from '@rio-cloud/rio-uikit/SortDirection';
import TableSettingsDialog from '@rio-cloud/rio-uikit/TableSettingsDialog';
import { format, sub } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useChargingActivity } from '@/api/chargingActivity';
import type { ChargingSessionType } from '@/api/schema';
import { Table } from '@/components/Table/Table';
import { TimePicker, timeFramePickerLabel } from '@/components/TimePicker';
import { DateFrame, type TimeFrame, TimePickerType } from '@/components/TimePicker/types';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import { DownloadDropdownButton } from '@/features/chargingActivity/components/DownloadDropdownButton/DownloadDropdownButton';
import ChargingSessionSidebar from '@/features/chargingActivity/components/Sidebar/ChargingSessionSidebar';
import SupportModal from '@/features/chargingActivity/components/SupportModal';
import Totals from '@/features/chargingActivity/components/Totals';
import { useChargingSessionColumns } from '@/features/chargingActivity/hooks/useChargingSessionColumns';
import { useTableSettings } from '@/hooks/useTableSettings';
import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

const ChargingActivity = () => {
    const intl = useIntl();
    const { locale } = intl;
    const [searchParams] = useSearchParams();
    const debugConfig = createDebugConfig();
    const { currency } = useUserStore();

    const timeFrameParam = getTimeFrameFromParams(intl, searchParams);
    const [timeFrame, setTimeFrame] = useState<TimeFrame>(getInitialTimeFrame(intl, timeFrameParam));

    const [session, setSession] = useState<ChargingSessionType | undefined>(undefined);
    const [open, setOpen] = useState<boolean>(false);

    const { data: sessions, isLoading } = useChargingActivity({
        filters: {
            dateRange: formatDateRange(timeFrame),
            pagination: { offset: 0 },
        },
    });

    const openSideBar = (s: ChargingSessionType) => {
        setOpen(true);
        setSession(s);
    };

    const dismissSideBar = () => {
        setOpen(false);
        setSession(undefined);
    };

    const cacheColumns = useChargingSessionColumns(t, currency, locale);

    const nonFilterableColumns = [
        'sessionStart',
        'duration',
        'energy',
        'totalCost',
        'sessionEnd',
        'plugType',
        'chargingStationName',
        'chargingStationAddress',
        'chargingStationZipCode',
        'chargingStationCity',
        'chargingStationCountry',
        'invoiceNumber',
        'invoiceDate',
    ];
    const defaultHiddenColumns = [
        'sessionEnd',
        'plugType',
        'chargingStationName',
        'chargingStationAddress',
        'chargingStationZipCode',
        'chargingStationCity',
        'chargingStationCountry',
        'invoiceNumber',
        'invoiceDate',
    ];

    const { columnOrder, hiddenColumns, visibleColumns, handleColumnChange, columnLabels } = useTableSettings(
        'chargingActivity',
        cacheColumns,
        defaultHiddenColumns
    );

    const isContainerLayout = useMemo(
        () =>
            ['plugType', 'chargingStationName', 'chargingStationAddress'].every(column =>
                hiddenColumns.includes(column)
            ),
        [hiddenColumns]
    );

    useEffect(() => {
        if (!isContainerLayout) {
            trackCustomEvent(
                'table_layout_change_fullwidth',
                {
                    trigger: 'click',
                    flow_name: 'charging_activity',
                },
                debugConfig
            );
        }
    }, [isContainerLayout, debugConfig]);

    const renderToolbar = () => (
        <div
            className='display-flex flex-1-1-0 align-items-end flex-wrap justify-content-between'
            style={{ marginTop: '12px' }}
        >
            <div className='display-flex align-items-center gap-15'>
                <TimePicker
                    timePickerType={TimePickerType.SHORT}
                    timeFrame={timeFrame}
                    setTimeFrame={setTimeFrame}
                    callback={(startDate, endDate) => {
                        if (startDate && endDate) {
                            setTimeFrame(prev => ({
                                ...prev,
                                startDate,
                                endDate,
                            }));
                        }
                    }}
                />
                <DownloadDropdownButton
                    disabled={sessions?.chargingSessions.length === 0}
                    filters={{
                        dateRange: formatDateRange(timeFrame),
                        pagination: { offset: 0 },
                    }}
                />
            </div>
            <SupportModal />
        </div>
    );

    const renderTableSettingsDialog = (showTableSettingsDialog: boolean, onHide: () => void) => {
        if (showTableSettingsDialog) {
            trackCustomEvent(
                'open_table_settings',
                { trigger: 'click', flow_name: 'charging_activity', element_name: 'button' },
                debugConfig
            );
        }

        return (
            <TableSettingsDialog
                show={showTableSettingsDialog}
                title={t('tableSettings.title')}
                defaultColumnOrder={cacheColumns.map(c => c.key)}
                columnOrder={columnOrder}
                columnLabels={columnLabels}
                disabledColumns={['sessionStart']}
                onHide={onHide}
                onColumnChange={handleColumnChange}
                defaultHiddenColumns={defaultHiddenColumns}
                hiddenColumns={hiddenColumns}
                resetButtonText={t('tableSettings.button.reset')}
                searchPlaceholder={t('tableSettings.search')}
                closeButtonText={t('general.close')}
                notFoundMessage={t('tableSettings.notFound')}
                immediateChange
            />
        );
    };

    return (
        <>
            <ApplicationLayout.Sidebar className='right'>
                <ChargingSessionSidebar session={session} open={open} dismiss={dismissSideBar} />
            </ApplicationLayout.Sidebar>
            <ApplicationLayout.Body className='responsive'>
                <div className={`clearance ${isContainerLayout && 'container'} fluid-default margin-x-auto`}>
                    <Table
                        data={sessions?.chargingSessions ?? []}
                        columns={visibleColumns}
                        isLoading={isLoading}
                        searchByLabel={t('general.searchBy')}
                        noMoreMessage={t('chargingSessions.everythingIsLoaded')}
                        noResults={{
                            title: t('chargingSessions.noResultsFound.title'),
                            description: t('chargingSessions.noResultsFound.description'),
                        }}
                        nonFilterableColumns={nonFilterableColumns}
                        isSidebarOpen={open}
                        openRowSidebar={row => openSideBar(row)}
                        overview={<Totals timeFrame={timeFrame} currency={currency} />}
                        showFilter
                        extraFilters={renderToolbar()}
                        initialSortKey='sessionStart'
                        initialSortDirection={SortDirection.DESCENDING}
                        showTableSettings
                        tableSettingsDialog={renderTableSettingsDialog}
                    />
                </div>
            </ApplicationLayout.Body>
        </>
    );
};

const getTimeFrameFromParams = (intl: IntlShape, searchParams: URLSearchParams): TimeFrame | undefined => {
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const timeFrameEnumParam = searchParams.get('timeFrame') as DateFrame;
    return startDateParam && endDateParam && timeFrameEnumParam
        ? {
              startDate: startDateParam ? new Date(startDateParam) : new Date(),
              endDate: new Date(endDateParam ?? ''),
              frame: {
                  timeEnum: timeFrameEnumParam,
                  timeLabel: timeFramePickerLabel(timeFrameEnumParam, intl),
              },
          }
        : undefined;
};

const getInitialTimeFrame = (intl: IntlShape, tf?: TimeFrame): TimeFrame =>
    tf ?? {
        startDate: sub(new Date(), { days: 7 }),
        endDate: new Date(),
        frame: {
            timeEnum: DateFrame['7DAYS'],
            timeLabel: timeFramePickerLabel(DateFrame['7DAYS'], intl),
        },
    };

const formatDateRange = (timeFrame: TimeFrame) => ({
    localStartDate: format(timeFrame.startDate ?? new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx"),
    localEndDate: format(timeFrame.endDate ?? new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx"),
});

export default ChargingActivity;
